'use client';

import { useUserStore } from '@unifyapps/carbon/auth/UserStoreProvider';
import type { Navigation } from '@unifyapps/carbon/components/NavigationContainer/types';
import type {
  NavTransformCustomizers,
  NavTransformUser,
} from '@unifyapps/carbon/components/NavigationContainer/utils/transformNavItem';
import { transformNavItem } from '@unifyapps/carbon/components/NavigationContainer/utils/transformNavItem';
import { EMPTY_ARRAY } from '@unifyapps/carbon/consts/empty';
import { InterfaceNavigationHelper } from '@unifyapps/carbon/helpers/InterfaceNavigationHelper';
import { useFetchInterfacePermissions } from '@unifyapps/carbon/no-code/access-control/hooks/useFetchInterfacePermissions';
import { useSideNavParams } from '@unifyapps/carbon/no-code/hooks/useSideNavParams';
import { useGlobalAppStateStore } from '@unifyapps/carbon/no-code/stores/app/GlobalAppStateStore';
import { InterfaceClient, InterfaceModes } from '@unifyapps/carbon/no-code/stores/InterfaceStore';
import InterfaceHelper from '@unifyapps/carbon/no-code/stores/InterfaceStore/helper/InterfaceHelper';
import type {
  InterfaceEntity,
  NavItem,
  NavigationResolution,
} from '@unifyapps/defs/types/interface';
import { useCallback, useEffect, useMemo } from 'react';
import { usePermissibleNavItems } from '../SideOrBottomNav/hooks/usePermissibleNavItems';

export type AppNavigationUser = NavTransformUser;

export type SecondaryCustomizers = NavTransformCustomizers;

type Args = {
  interfaceRecord?: InterfaceEntity;
  resolution: NavigationResolution;
  unopenedNotificationsCount?: number;
};

export function useAppNavigationModel({
  interfaceRecord,
  resolution,
  unopenedNotificationsCount,
}: Args) {
  const { setNavigationState } = useGlobalAppStateStore().hooks.useActions();

  // sync unread notification count to global store for compute context
  useEffect(() => {
    setNavigationState({ unopenedNotificationsCount: unopenedNotificationsCount ?? 0 });
  }, [unopenedNotificationsCount, setNavigationState]);

  const data = useUserStore().hooks.useCurrentUserDetails();
  const user = useMemo<AppNavigationUser>(
    () => ({
      name: data?.user?.name,
      email: data?.user?.email || data?.user?.username,
      id: data?.user?.id,
    }),
    [data?.user?.email, data?.user?.id, data?.user?.name, data?.user?.username],
  );

  const { permissions } = useFetchInterfacePermissions({
    mode: InterfaceModes.RUNNER,
    interfaceId: interfaceRecord?.id,
    client: InterfaceClient.UA_MATRIX,
  });

  const permissiblePrimaryNavItems = usePermissibleNavItems({
    items: interfaceRecord
      ? InterfaceNavigationHelper.getPrimaryNavigationByResolution(interfaceRecord, resolution)
      : (EMPTY_ARRAY as NavItem[]),
    permissions,
    pagePermissions: interfaceRecord?.properties.pagePermissions,
  });

  const permissibleSecondaryNavItems = usePermissibleNavItems({
    items: interfaceRecord
      ? InterfaceNavigationHelper.getSecondaryItemsByResolution(interfaceRecord, resolution)
      : (EMPTY_ARRAY as NavItem[]),
    // when app is public, hide userMenu through implicit permissions
    permissions: {
      ...permissions,
      navigation: {
        ...permissions.navigation,
        USER_MENU: InterfaceHelper.getInterfaceSecurityDetails(interfaceRecord)?.type !== 'PUBLIC',
      },
    },
    pagePermissions: interfaceRecord?.properties.pagePermissions,
  });

  const app = useMemo(
    () => ({
      name: interfaceRecord?.properties.name ?? '',
      logo: {
        small: interfaceRecord?.properties.metadata.logo.small ?? '',
        large: interfaceRecord?.properties.metadata.logo.large ?? '',
      },
    }),
    [
      interfaceRecord?.properties.metadata.logo.large,
      interfaceRecord?.properties.metadata.logo.small,
      interfaceRecord?.properties.name,
    ],
  );

  const buildNavigation = useCallback(
    (customizers: SecondaryCustomizers): Navigation | undefined => {
      if (!interfaceRecord?.properties) return undefined;

      const baseCtx = {
        interfaceRecord,
        mode: InterfaceModes.RUNNER,
        customizers,
        user,
      };

      return {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- permissiblePrimaryNavItems will not be undefined
        primary: permissiblePrimaryNavItems!
          .map((item) => transformNavItem(item, { ...baseCtx, variant: 'primary' }))
          .filter(Boolean) as Navigation['primary'],
        secondary: permissibleSecondaryNavItems
          ?.map((item) => transformNavItem(item, { ...baseCtx, variant: 'secondary' }))
          .filter(Boolean) as Navigation['secondary'],
      };
    },
    [permissiblePrimaryNavItems, permissibleSecondaryNavItems, interfaceRecord, user],
  );

  const navigationParams = useSideNavParams({ interfaceRecord, resolution });

  return {
    buildNavigation,
    app,
    user,
    permissions,
    navigationParams,
  };
}
